import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import { useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button/button";
import {
  BwChevronContractMdVericalLine,
  BwChevronExpandMdVericalLine,
} from "../icons";
import { Separator } from "../separator";

/**
 * Sub-pixel slack before the Expand control is worth showing. `contentRect` is
 * fractional, so without this a box landing a hair over `maxHeight` grows a
 * control nobody needs — and flickers it on and off as a column is dragged
 * across the threshold.
 */
const OVERFLOW_TOLERANCE = 1;

export type ShowMoreTextProps = React.PropsWithChildren & {
  /**
   * Class on the clipping box. It is the box whose `height` this component
   * writes, so padding here is measured against `box-sizing: border-box` and
   * would under-size the content — put padding on a wrapper instead.
   */
  className?: string;
  /** Class on the fade overlay drawn over the clipped tail while collapsed. */
  overflowClassName?: string;
  /** Collapsed height, in px. Default 200. */
  maxHeight?: number;
};

/**
 * Clips its children to `maxHeight` and grows an Expand/Collapse control once
 * they overflow.
 *
 * ## The content is measured through a second element
 *
 * The clipping box's own height is what this component writes, so it cannot
 * also be what this component reads: `scrollHeight` is never less than an
 * element's client height, so re-measuring the box it has already pinned can
 * report growth and never a shrink — which is how a resizable column ended up
 * holding a stale height with dead space under the text. The inner wrapper is
 * never height-constrained, so its box IS the natural height of the content,
 * in both directions.
 *
 * `flow-root` on that wrapper is load-bearing. The first and last child's
 * vertical margins would otherwise collapse straight through a plain `div` and
 * out to the clipping box, leaving them out of the measurement and clipping the
 * content by exactly that much. The clipping box contains them today only
 * because `overflow-hidden` makes it a block formatting context; `flow-root` is
 * the same containment with no clipping.
 *
 * ## Why an observer, and why nothing is ever measured by hand
 *
 * Height depends on width, and a flex resize changes neither `children` nor
 * `maxHeight` — so a dependency array cannot see it. Nor can it see an image or
 * a webfont landing after first paint.
 *
 * It is equally important that nothing here calls `getBoundingClientRect`,
 * `offsetHeight` or `scrollHeight`. Those force a synchronous document layout,
 * and this mounts inside the inbox's order panel at the exact moment that
 * panel's 300ms width transition starts — see the warning in
 * `features/office/inbox/_components/inbox-panels.tsx`: a forced layout there
 * fires the panel library's own observer and cancels the open. `contentRect` is
 * computed by the browser during its own layout step and costs nothing.
 */
export const ShowMoreText = ({
  children,
  overflowClassName,
  className,
  maxHeight = 200, // default 200px
}: ShowMoreTextProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, toggleExpanded] = useToggle(false);
  /**
   * The content's natural height, or `null` until the first measurement.
   * A scalar on purpose: a drag changes the width on every frame but the height
   * only on some, and `Object.is` turns the rest into no re-render at all.
   */
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  /**
   * True for the render that follows a click on Expand/Collapse, and nothing
   * else — a toggle is the only height change a person asked to watch. A ref
   * rather than state because it must ride along with an update that is already
   * happening instead of causing one, and because the observer has to clear it
   * without forcing a render when the height is unchanged. A lingering `true`
   * is harmless: every other route to a new height runs through the observer,
   * which clears it first, and a render that changes no height starts no
   * transition.
   */
  const shouldAnimateRef = useRef(false);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      shouldAnimateRef.current = false;
      // Rounded up, as `scrollHeight` was: pinning a block formatting context to
      // a fractional child height can shave a descender, and whole pixels keep a
      // drag from re-rendering on every sub-pixel reflow.
      setContentHeight(Math.ceil(entry.contentRect.height));
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  // Derived, never stored — a second copy of this is what went stale. `!== null`
  // and not a truthiness test: empty children measure a legitimate 0.
  const isOverflowing =
    contentHeight !== null && contentHeight > maxHeight + OVERFLOW_TOLERANCE;

  const height =
    contentHeight === null
      ? undefined
      : isExpanded || !isOverflowing
        ? contentHeight
        : maxHeight;

  return (
    <div className="relative">
      <div
        style={{
          height,
          // Frame 1 only. The observer's first delivery lands before paint, but
          // the React update it schedules does not — this is what keeps that
          // frame from flashing the full text. Dropped the moment a measurement
          // exists, when the clamp is either redundant or actively wrong.
          maxHeight: contentHeight === null ? maxHeight : undefined,
        }}
        className={cn(
          /* sorted */
          "overflow-hidden",
          "transition-[height] ease-in-out motion-reduce:transition-none",
          // The browser's timeline owns the tween, so a parent re-rendering on
          // every frame of a drag cannot reset or starve it — the reasoning
          // `tabs-indicator` documents. Motion cannot do this: it queues every
          // height write onto the next frame, `duration: 0` included.
          shouldAnimateRef.current ? "duration-[250ms]" : "duration-0",
          className,
        )}
      >
        <div ref={contentRef} className="flow-root">
          {children}
        </div>
        {!isExpanded && isOverflowing && (
          <div
            className={cn(
              /* sorted */
              "absolute left-0 bottom-8",
              "w-full h-12",
              "pointer-events-none",
              overflowClassName,
            )}
          />
        )}
      </div>

      {isOverflowing && (
        <div className="w-full flex items-center gap-2 relative">
          {!isExpanded && (
            <div className="bg-linear-to-b from-transparent to-bg-light w-full h-8 absolute bottom-full left-0 pointer-events-none"></div>
          )}
          <Separator className="flex-1" />
          <Button
            kind="outline"
            variant="neutral"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              shouldAnimateRef.current = true;
              toggleExpanded();
            }}
            className="font-normal"
          >
            {isExpanded ? "Collapse" : "Expand"}
            {isExpanded ? (
              <BwChevronContractMdVericalLine />
            ) : (
              <BwChevronExpandMdVericalLine />
            )}
          </Button>
          <Separator className="flex-1" />
        </div>
      )}
    </div>
  );
};
